import { Injectable, inject } from '@angular/core';
import { httpOptions } from '../_helpers/utils';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {
  OrganisationCreate,
  OrganisationIndex,
  OrganisationIndexView,
  OrganisationOverviewIndex,
} from '../_models/organisation';
import { environment } from 'src/environments/environment';
import { PaginationData } from '../_models/pagination-data.model';
import { InformationBanner } from '../_models/information-banner';
import { LicenseType } from '../_models/license-type';

@Injectable({
  providedIn: 'root',
})
export class OrganisationService {
  private readonly http = inject(HttpClient);

  httpOptions = httpOptions;

  private readonly pageNumber$ = new BehaviorSubject<number>(null);
  private readonly pageSize$ = new BehaviorSubject<number>(null);
  private readonly createOrgCache$ = new BehaviorSubject<OrganisationCreate>(
    null,
  );

  public get retrieveAndClearUnsavedOrgCache(): OrganisationCreate {
    const cached = this.createOrgCache$.value;
    this.clearUnsavedCache();
    return cached;
  }

  clearUnsavedCache(): void {
    this.createOrgCache$.next(null);
  }

  onUpdateCreateOrgCache = (org: OrganisationCreate): void => {
    this.createOrgCache$.next(org);
  };

  onUpdatePageNumber = (pageNumber: number): void => {
    this.pageNumber$.next(pageNumber);
  };

  onUpdatePageSize = (pageSize: number): void => {
    this.pageSize$.next(pageSize);
  };

  public get paginationCache(): { pageNumber: number; pageSize: number } {
    if (this.pageNumber$.value !== null && this.pageSize$.value !== null) {
      return {
        pageNumber: this.pageNumber$.value,
        pageSize: this.pageSize$.value,
      };
    } else {
      return null;
    }
  }

  getOrganisations(
    sortBy = 'Errors',
    sortDirection = 'desc',
    pageNumber = 1,
    pageSize = 100,
    search: string = null,
  ): Observable<OrganisationOverviewIndex> {
    let url = `${environment.apiUrl}/organisations?orderBy=${sortBy}&orderByDirection=${sortDirection}&pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (search !== null) {
      url = url + `&search=${search}`;
    }
    return this.http
      .get<OrganisationOverviewIndex['data']>(url, this.httpOptions)
      .pipe(
        map((response) => {
          // Set pagination data
          const paginationData = JSON.parse(
            response.headers.get('X-Pagination'),
          ) as PaginationData;
          const data = response.body;
          return { data, paginationData };
        }),
      );
  }

  getOrganisation(id: number): Observable<OrganisationIndexView> {
    return this.http
      .get<OrganisationIndexView>(
        `${environment.apiUrl}/organisations/${id}`,
        this.httpOptions,
      )
      .pipe(
        map((response) => {
          return response.body;
        }),
      );
  }

  createOrganisation(orgCreate: OrganisationCreate): Observable<string> {
    return this.http
      .post<string>(
        `${environment.apiUrl}/organisations`,
        orgCreate,
        this.httpOptions,
      )
      .pipe(
        map((response) => {
          if (response.body == null) {
            throw {
              status: response.status,
              message: 'Something went wrong',
              code: null,
            };
          }
          return response.body;
        }),
      );
  }

  updateOrganisation(orgName: string, orgId: number): Observable<string> {
    return this.http
      .patch<string>(
        `${environment.apiUrl}/organisations/${orgId}`,
        { organisationName: orgName },
        this.httpOptions,
      )
      .pipe(
        map((response) => {
          switch (response.status) {
            case 200:
              return 'Organisation successfully updated.';
            case 201:
              return 'Update organisation request is pending approval; the status can be checked on the approvals page.';
            default:
              throw Error('Something went wrong');
          }
        }),
      );
  }

  updateLicenseType(
    orgId: number,
    licenseType: LicenseType,
  ): Observable<{ message: string; approvalPending: boolean }> {
    return this.http
      .patch<string>(
        `${environment.apiUrl}/organisations/${orgId}/licence-type`,
        { licenseType: licenseType },
        this.httpOptions,
      )
      .pipe(
        map((response) => {
          switch (response.status) {
            case 200:
              return { message: 'Licence successfully updated.', approvalPending: false };
            case 201:
              return {
                message: 'Update licence request is pending approval; the status can be checked on the approvals page.',
                approvalPending: true,
              };
            default:
              throw Error('Something went wrong');
          }
        }),
      );
  }

  deleteOrganisation(orgId: number): Observable<string> {
    return this.http
      .delete<string>(`${environment.apiUrl}/organisations/${orgId}`)
      .pipe(
        map(() => {
          return 'Delete organisation request is pending approval; the status can be checked on the approvals page.';
        }),
      );
  }

  updateInformationBanner(
    informationBanner: InformationBanner,
  ): Observable<InformationBanner> {
    return this.http
      .post<InformationBanner>(
        `${environment.apiUrl}/organisations/information-banner`,
        informationBanner,
        this.httpOptions,
      )
      .pipe(map((response) => response.body));
  }

  updateRequire2FA(orgId: number, require2FA: boolean): Observable<string> {
    return this.http
      .patch<string>(
        `${environment.apiUrl}/organisations/${orgId}/two-factor-required`,
        { required: require2FA },
        this.httpOptions,
      )
      .pipe(map((response) => response.body));
  }
}
